import React, { useEffect, useState } from "react";
import { Box, Paper, TextField, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Select, MenuItem, useTheme, useMediaQuery, Dialog, Typography, Card } from "@mui/material"; // <-- Added Dialog, Typography, Card
import SearchIcon from "@mui/icons-material/Search";
import axios from "axios";

const AttendanceApp = () => {
  const [workerNames, setWorkerNames] = useState([]);
  const [workerTimes, setWorkerTimes] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [currentTime, setCurrentTime] = useState(new Date());
  const [attendanceEnabled, setAttendanceEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [attendedWorkers, setAttendedWorkers] = useState([]); // store attended workers' IDs
  const [popup, setPopup] = useState({ open: false, success: true, message: "" }); // <-- Add popup state

  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    const fetchWorkerNames = async () => {
      try {
        const response = await axios.get("https://enzo-matr.onrender.com/api/workers");
        setWorkerNames(response.data);
      } catch (error) {
        console.error("❌ Error fetching worker names:", error);
      }
    };
    fetchWorkerNames();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch attended workers for today
  useEffect(() => {
    const fetchAttendances = async () => {
      try {
        const res = await axios.get("https://enzo-matr.onrender.com/attendances?date=" + today);
        setAttendedWorkers(res.data.map(a => a.worker)); // assuming worker name is unique

        // NEW: Populate workerTimes with saved attendance data
        const timesObj = {};
        res.data.forEach(a => {
          // Find worker by name to get _id
          const worker = workerNames.find(w => w.name === a.worker);
          if (worker) {
            timesObj[worker._id] = {
              login: a.login ? convertTo24Hour(a.login) : "",
              logout: a.logout ? convertTo24Hour(a.logout) : "",
              state: a.state || "Absent"
            };
          }
        });
        setWorkerTimes(timesObj);
      } catch (e) {
        setAttendedWorkers([]);
        setWorkerTimes({});
      }
    };
    fetchAttendances();
    // Add workerNames to dependency so it runs after workers are loaded
  }, [today, saving, workerNames]);

  const calculateTotalHours = (login, logout) => {
    if (!login || !logout) return "";
    const [loginHr, loginMin] = login.split(":").map(Number);
    const [logoutHr, logoutMin] = logout.split(":").map(Number);
    if (
      isNaN(loginHr) || isNaN(loginMin) ||
      isNaN(logoutHr) || isNaN(logoutMin)
    ) {
      return "";
    }
    const loginMinutes = loginHr * 60 + loginMin;
    const logoutMinutes = logoutHr * 60 + logoutMin;
    if (logoutMinutes < loginMinutes) return "";
    const totalMinutes = logoutMinutes - loginMinutes;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours} Hr${minutes > 0 ? ` ${minutes} Min` : ""}`;
  };

  // Utility to convert "HH:mm" (24hr) to "hh:mm AM/PM" (12hr)
  function to12HourFormat(time24) {
    if (!time24) return "";
    let [hour, minute] = time24.split(":").map(Number);
    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12;
    return `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")} ${ampm}`;
  }

  // Add this utility function at the top-level (outside the component or inside, before useEffect):
  function convertTo24Hour(time12h) {
    // Expects "hh:mm AM/PM", returns "HH:mm"
    if (!time12h) return "";
    const [time, modifier] = time12h.split(" ");
    let [hours, minutes] = time.split(":").map(Number);
    if (modifier === "PM" && hours !== 12) hours += 12;
    if (modifier === "AM" && hours === 12) hours = 0;
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
  }

  // State dropdown handler
  const handleStateChange = async (workerId, value) => {
    setWorkerTimes(prev => ({
      ...prev,
      [workerId]: {
        ...prev[workerId],
        state: value,
      }
    }));

    // Save attendance for this worker only
    const worker = workerNames.find(w => w._id === workerId);
    const times = workerTimes[workerId] || {};
    const login = times.login ? to12HourFormat(times.login) : "";
    const logout = times.logout ? to12HourFormat(times.logout) : "";
    const total = login && logout ? calculateTotalHours(times.login, times.logout) : "";
    const state = value;

    // Only save if state is set and login/logout are filled
    if (!state || !login || !logout || !total) return;

    try {
      await axios.post("https://enzo-matr.onrender.com/attendances", {
        date: today,
        worker: worker.name,
        login,
        logout,
        total,
        state,
      });
      setPopup({ open: true, success: true, message: `Attendance stored for ${worker.name}` }); // <-- Green tick
      setAttendedWorkers(prev => [...prev, worker.name]);
    } catch (error) {
      setPopup({ open: true, success: false, message: "Error saving attendance or already added for today." }); // <-- Red cross
    }
  };

  // Time change handler
  const handleTimeChange = (workerId, field, value) => {
    setWorkerTimes(prev => ({
      ...prev,
      [workerId]: {
        ...prev[workerId],
        [field]: value
      }
    }));
  };

  // Get state for dropdown
  const getWorkerState = (workerId) => {
    const times = workerTimes[workerId] || {};
    return times.state || (times.login && times.logout ? "Present" : "Absent");
  };

  // Save attendance
  const handleSaveAttendance = async () => {
    setSaving(true);
    let duplicateFound = false;
    let storedAny = false; // Track if any attendance was stored

    for (const worker of workerNames) {
      // Only save if not already attended today
      if (attendedWorkers.includes(worker.name)) continue;
      const times = workerTimes[worker._id] || {};
      const login = times.login ? to12HourFormat(times.login) : "";
      const logout = times.logout ? to12HourFormat(times.logout) : "";
      const total = login && logout ? calculateTotalHours(times.login, times.logout) : "";
      const state = getWorkerState(worker._id);

      // Only save if state is set and login/logout are filled
      if (!state || !login || !logout || !total) continue;

      try {
        await axios.post("https://enzo-matr.onrender.com/attendances", {
          date: today,
          worker: worker.name,
          login,
          logout,
          total,
          state,
        });
        storedAny = true; // Mark as stored
      } catch (error) {
        duplicateFound = true;
      }
    }
    setSaving(false);
    setAttendanceEnabled(false);

    if (storedAny) {
      setPopup({ open: true, success: true, message: "Successfully stored" }); // <-- Green tick
    } else if (duplicateFound) {
      setPopup({ open: true, success: false, message: "Some attendance records were already added for today." }); // <-- Red cross
    }
  };

  // Filter: show only attended workers if attendance is not enabled, and filter by search term
  const displayedWorkers = (attendanceEnabled
    ? workerNames
    : workerNames.filter(w => attendedWorkers.includes(w.name))
  ).filter(w =>
    w.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Responsive hooks
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
          marginLeft: isMobileOrTablet ? 0 : "790px",
          marginTop: isMobileOrTablet ? 2 : "-525px",
          position: isMobileOrTablet ? "static" : "relative",
          top: isMobileOrTablet ? 0 : "-25px",
          width: isMobileOrTablet ? "98vw" : "720px",
          mx: isMobileOrTablet ? "auto" : undefined,
          mb: isMobileOrTablet ? 2 : undefined,
        }}
      >
        <Paper
          sx={{
            width: "100%",
            height: isMobileOrTablet ? "380px" : "auto",
            maxWidth: isMobileOrTablet ? "92vw" : "680px",
            padding: isMobileOrTablet ? 1 : 2,
            ml: isMobileOrTablet ? -2 : 0,
            borderRadius: "10px",
            boxShadow: 3,
          }}
        >
          {/* Date & Time Row */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
              flexDirection: isMobileOrTablet ? "column" : "row",
              gap: isMobileOrTablet ? 1 : 0,
            }}
          >
            <Box sx={{ fontWeight: "bold", fontSize: isMobileOrTablet ? "15px" : "18px", fontFamily: "caladea" }}>
              {today}
            </Box>
            <Box sx={{ fontWeight: "bold", fontSize: isMobileOrTablet ? "15px" : "18px", fontFamily: "caladea" }}>
              {currentTime.toLocaleTimeString()}
            </Box>
          </Box>
          {/* Search Bar */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
            <TextField
              fullWidth
              placeholder="Search People etc."
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                backgroundColor: "white",
                borderRadius: "12px",
                "& fieldset": { borderRadius: "12px" },
                fontSize: isMobileOrTablet ? "13px" : undefined,
                height: isMobileOrTablet ? "36px" : undefined,
              }}
              size={isMobileOrTablet ? "small" : "medium"}
            />
            <Box
              sx={{
                backgroundColor: "black",
                width: isMobileOrTablet ? "32px" : "42px",
                height: isMobileOrTablet ? "32px" : "42px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "10px",
                cursor: "pointer"
              }}
            >
              <SearchIcon sx={{ color: "white", fontSize: isMobileOrTablet ? "18px" : undefined }} />
            </Box>
          </Box>
          <TableContainer
            sx={{
              overflowX: "auto",
              borderRadius: "12px",
              marginTop: "10px",
              height: isMobileOrTablet ? "180px" : "323px",
              overflowY: "auto",
              position: "relative",
              "&::-webkit-scrollbar": { display: "none" },
              "-ms-overflow-style": "none",
              "scrollbar-width": "none"
            }}
          >
            <Table size={isMobileOrTablet ? "small" : "medium"} sx={{ minWidth: 330 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "black", position: "sticky", top: 0, zIndex: 1 }}>
                  {["Date", "Worker", "Login", "Logout", "Total", "State"].map((head) => (
                    <TableCell key={head} sx={{ fontWeight: "bold", color: "white", fontSize: isMobileOrTablet ? "10px" : "12px", padding: isMobileOrTablet ? "4px" : "8px" }}>
                      {head}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedWorkers.length > 0 ? (
                  displayedWorkers.map((worker) => (
                    <TableRow key={worker._id}>
                      <TableCell sx={{ fontSize: isMobileOrTablet ? "10px" : "14px", padding: isMobileOrTablet ? "4px" : "6px", fontFamily: "Bricolage Grotesque", }}>{today}</TableCell>
                      <TableCell sx={{ fontSize: isMobileOrTablet ? "10px" : "14px", padding: isMobileOrTablet ? "4px" : "6px", fontFamily: "Bricolage Grotesque", }}>{worker.name}</TableCell>
                      <TableCell sx={{ fontSize: isMobileOrTablet ? "10px" : "14px", padding: isMobileOrTablet ? "4px" : "6px", fontFamily: "Bricolage Grotesque", }}>
                        <TextField
                          type="time"
                          size="small"
                          value={workerTimes[worker._id]?.login || ""}
                          onChange={e => handleTimeChange(worker._id, "login", e.target.value)}
                          sx={{ width: isMobileOrTablet ? 60 : 100 }}
                          disabled={!attendanceEnabled}
                          inputProps={{ style: { fontSize: isMobileOrTablet ? "10px" : undefined } }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: isMobileOrTablet ? "10px" : "14px", padding: isMobileOrTablet ? "4px" : "6px", fontFamily: "Bricolage Grotesque", }}>
                        <TextField
                          type="time"
                          size="small"
                          value={workerTimes[worker._id]?.logout || ""}
                          onChange={e => handleTimeChange(worker._id, "logout", e.target.value)}
                          sx={{ width: isMobileOrTablet ? 60 : 100 }}
                          disabled={!attendanceEnabled}
                          inputProps={{ style: { fontSize: isMobileOrTablet ? "10px" : undefined } }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: isMobileOrTablet ? "10px" : "14px", padding: isMobileOrTablet ? "4px" : "6px", fontFamily: "Bricolage Grotesque", }}>
                        {calculateTotalHours(workerTimes[worker._id]?.login, workerTimes[worker._id]?.logout)}
                      </TableCell>
                      <TableCell sx={{ fontSize: isMobileOrTablet ? "10px" : "14px", padding: isMobileOrTablet ? "4px" : "6px", fontFamily: "Bricolage Grotesque", }}>
                        {attendedWorkers.includes(worker.name) ? (
                          // Show state as colored box (green for Present, red for Absent)
                          <Box
                            sx={{
                              width: isMobileOrTablet ? 70 : 110,
                              backgroundColor:
                                (workerTimes[worker._id]?.state || "Absent").toLowerCase() === "present"
                                  ? "#28A745"
                                  : "#bb1726",
                              color: "white",
                              fontWeight: "bold",
                              borderRadius: "6px",
                              fontSize: isMobileOrTablet ? "10px" : "14px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              height: "32px"
                            }}
                          >
                            {(workerTimes[worker._id]?.state || "ABSENT").toUpperCase()}
                          </Box>
                        ) : (
                          <Select
                            value={getWorkerState(worker._id)}
                            onChange={e => handleStateChange(worker._id, e.target.value)}
                            disabled={!attendanceEnabled}
                            size="small"
                            sx={{
                              width: isMobileOrTablet ? 70 : 110,
                              backgroundColor: getWorkerState(worker._id) === "Present" ? "#28A745" : "#bb1726",
                              color: "white",
                              fontWeight: "bold",
                              borderRadius: "6px",
                              fontSize: isMobileOrTablet ? "10px" : undefined,
                              "& .MuiSelect-icon": { color: "white" },
                              "& .MuiOutlinedInput-notchedOutline": { border: "none" }
                            }}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  bgcolor: "#222",
                                  color: "white"
                                }
                              }
                            }}
                          >
                            <MenuItem value="Present" sx={{ bgcolor: "#28A745", color: "white", fontWeight: "bold", fontSize: isMobileOrTablet ? "10px" : undefined }}>PRESENT</MenuItem>
                            <MenuItem value="Absent" sx={{ bgcolor: "#bb1726", color: "white", fontWeight: "bold", fontSize: isMobileOrTablet ? "10px" : undefined }}>ABSENT</MenuItem>
                          </Select>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: "center", fontSize: isMobileOrTablet ? "10px" : "12px", padding: isMobileOrTablet ? "4px" : "6px" }}>
                      No workers found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {/* Add Attendance Button */}
          <Box sx={{ width: "100%", height: isMobileOrTablet ? "60px" : "90px", display: "flex", justifyContent: "center", marginTop: isMobileOrTablet ? "50px":"8px", }}>
            <Button
              fullWidth
              variant="contained"
              sx={{
                maxWidth: isMobileOrTablet ? "98vw" : "710px",
                backgroundColor: "black",
                color: "white",
                fontSize: isMobileOrTablet ? "16px" : "25px",
                fontFamily: "caladea",
                fontWeight: "bold",
                py: 1,
                borderRadius: "15px",
                textTransform: "none",
                ml: isMobileOrTablet ? 0 : "10px"
              }}
              disabled={saving}
              onClick={() => {
                if (attendanceEnabled) {
                  handleSaveAttendance();
                } else {
                  setAttendanceEnabled(true);
                }
              }}
            >
              {attendanceEnabled ? (saving ? "Saving..." : "SAVE ATTENDANCE") : "ADD ATTENDANCE"}
            </Button>
          </Box>
        </Paper>
      </Box>

      {/* Popup Dialog */}
      <Dialog
        open={popup.open}
        onClose={() => setPopup({ ...popup, open: false })}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "16px", textAlign: "center", p: 2 }
        }}
      >
        <Card sx={{ borderRadius: 3, p: 2, minWidth: 300, textAlign: "center" }}>
          <Box display="flex" flexDirection="column" alignItems="center" mt={1}>
            {popup.success ? (
              // Green Tick SVG
              <svg width="60" height="60" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="12" fill="#4caf50"/>
                <path d="M7 13l3 3 7-7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              // Red Cross SVG
              <svg width="60" height="60" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="12" fill="#bb1726"/>
                <path d="M8 8l8 8M16 8l-8 8" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
            <Typography
              variant="h6"
              align="center"
              sx={{
                fontWeight: "bold",
                color: popup.success ? "#4caf50" : "#bb1726",
                mt: 2
              }}
            >
              {popup.message}
            </Typography>
            <Button
              variant="contained"
              onClick={() => setPopup({ ...popup, open: false })}
              sx={{
                background: popup.success ? "#4caf50" : "#bb1726",
                color: "white",
                fontWeight: "bold",
                mt: 2
              }}
            >
              OK
            </Button>
          </Box>
        </Card>
      </Dialog>
    </>
  );
};

export default AttendanceApp;